import React, { useState, useRef, useEffect } from 'react';
import { FriendRequest, FriendshipTier } from '../types/friends';
import Modal from '../../../shared/components/ui/Modal';

interface PendingRequestCardProps {
  request: FriendRequest;
  isIncoming: boolean;
  onRespond: (
    requestId: string, 
    action: 'accept' | 'accept_lower' | 'reject' | 'request_higher',
    tier?: FriendshipTier,
    note?: string
  ) => Promise<void>;
}

const TIER_LABELS: Record<FriendshipTier, string> = {
  'TIER_1': 'Gold (Tier 1)',
  'TIER_2': 'Silver (Tier 2)',
  'TIER_3': 'Bronze (Tier 3)',
  'BASIC': 'Basic'
};

const TIER_COLORS: Record<FriendshipTier, string> = {
  'TIER_1': 'text-yellow-500',
  'TIER_2': 'text-gray-400',
  'TIER_3': '', // Custom color #A75217 applied via inline style
  'BASIC': 'text-gray-300'
};

// Tier hierarchy for comparison
const TIER_HIERARCHY: FriendshipTier[] = ['TIER_1', 'TIER_2', 'TIER_3', 'BASIC'];

export const PendingRequestCard: React.FC<PendingRequestCardProps> = ({ request, isIncoming, onRespond }) => {
  const [isNegotiating, setIsNegotiating] = useState(false);
  const [negotiationType, setNegotiationType] = useState<'accept_lower' | 'request_higher' | null>(null);
  const [selectedTier, setSelectedTier] = useState<FriendshipTier>('BASIC');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNegotiateDropdown, setShowNegotiateDropdown] = useState(false);
  const negotiateRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (negotiateRef.current && !negotiateRef.current.contains(event.target as Node)) {
        setShowNegotiateDropdown(false);
      }
    };

    if (showNegotiateDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNegotiateDropdown]);

  const handleNegotiate = (type: 'accept_lower' | 'request_higher') => {
    setNegotiationType(type);
    setIsNegotiating(true);
    setShowNegotiateDropdown(false);
    
    // Set default tier based on negotiation type
    if (type === 'accept_lower') {
      // For accept lower, default to one tier below requested
      const requestedIndex = TIER_HIERARCHY.indexOf(request.requestedTier);
      const lowerTier = TIER_HIERARCHY[requestedIndex + 1] || 'BASIC';
      setSelectedTier(lowerTier);
    } else {
      // For request higher, default to one tier above requested
      const requestedIndex = TIER_HIERARCHY.indexOf(request.requestedTier);
      const higherTier = TIER_HIERARCHY[requestedIndex - 1] || 'TIER_1';
      setSelectedTier(higherTier);
    }
  };
  
  // Get the effective tier to negotiate against
  // For COUNTER_PENDING incoming requests, use counter_requested_tier
  // For PENDING incoming requests, use requested_tier
  const getEffectiveTier = (): FriendshipTier => {
    if (isIncoming && request.status === 'COUNTER_PENDING' && request.counterRequestedTier) {
      return request.counterRequestedTier;
    }
    return request.requestedTier;
  };
  
  // Get available tiers based on negotiation type
  const getAvailableTiers = (): FriendshipTier[] => {
    const effectiveTier = getEffectiveTier();
    const requestedIndex = TIER_HIERARCHY.indexOf(effectiveTier);
    
    if (negotiationType === 'accept_lower') {
      // Show tiers below the requested tier
      return TIER_HIERARCHY.slice(requestedIndex + 1);
    } else if (negotiationType === 'request_higher') {
      // Show tiers above the requested tier
      return TIER_HIERARCHY.slice(0, requestedIndex);
    }
    
    return TIER_HIERARCHY;
  };
  
  // Determine which negotiate options to show
  const canAcceptLower = () => {
    const effectiveTier = getEffectiveTier();
    const requestedIndex = TIER_HIERARCHY.indexOf(effectiveTier);
    return requestedIndex < TIER_HIERARCHY.length - 1; // Not already at lowest tier
  };
  
  const canRequestHigher = () => {
    const effectiveTier = getEffectiveTier();
    const requestedIndex = TIER_HIERARCHY.indexOf(effectiveTier);
    return requestedIndex > 0; // Not already at highest tier
  };

  const handleSubmitNegotiation = async () => {
    if (!negotiationType) return;
    setIsSubmitting(true);
    try {
      await onRespond(request.id, negotiationType, selectedTier, note);
      setIsNegotiating(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTierBadge = (tier: FriendshipTier) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800`}>
      <span 
        className={`mr-1 ${TIER_COLORS[tier] || ''}`}
        style={tier === 'TIER_3' ? { color: '#A75217' } : undefined}
      >
        ★
      </span>
      {TIER_LABELS[tier]}
    </span>
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        {/* Top Row: Avatar + Info */}
        <div className="flex items-start flex-1 min-w-0">
          <div className="flex-shrink-0">
            {request.profilePictureUrl ? (
              <img className="h-14 w-14 rounded-full object-cover" src={request.profilePictureUrl} alt="" />
            ) : (
              <div className="h-14 w-14 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-medium text-xl">
                {request.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          
          <div className="ml-4 flex-1 min-w-0">
            <div className="flex items-center space-x-2 flex-wrap md:flex-nowrap">
              <h3 className="text-base font-semibold text-gray-900 break-words md:truncate">{request.name}</h3>
              <div className="flex items-center space-x-2 flex-shrink-0">
                <span className="text-xs text-gray-500">Requested:</span>
                {renderTierBadge(request.requestedTier)}
              </div>
            </div>
            <p className="text-sm text-gray-500 mb-2">@{request.username}</p>
            
            {request.note && (
              <p className="text-sm text-gray-600 italic mb-2">"{request.note}"</p>
            )}
            
            {request.status === 'COUNTER_PENDING' && request.counterRequestedTier && (
              <div className="inline-flex items-center space-x-2 p-2 bg-yellow-50 rounded text-xs text-yellow-800 border border-yellow-100">
                <strong>{isIncoming ? 'They countered with:' : 'You countered with:'}</strong>
                {renderTierBadge(request.counterRequestedTier)}
                {request.counterNote && <span className="italic ml-2">"{request.counterNote}"</span>}
              </div>
            )}
          </div>
        </div>

        {/* Bottom Row: Actions (mobile) / Right Side (desktop) */}
        {isIncoming && (
          <div className="flex items-center justify-end space-x-2 mt-3 pt-3 border-t border-gray-100 md:mt-0 md:pt-0 md:border-t-0 md:ml-4 md:flex-shrink-0">
            <button
              onClick={() => onRespond(request.id, 'accept')}
              className="inline-flex justify-center px-4 py-2 border border-green-600 text-sm font-medium rounded-md text-green-600 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600"
            >
              Accept
            </button>
            <button
              onClick={() => onRespond(request.id, 'reject')}
              className="inline-flex justify-center px-4 py-2 border border-red-200 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Reject
            </button>
            <div className="relative" ref={negotiateRef}>
              <button 
                onClick={() => setShowNegotiateDropdown(!showNegotiateDropdown)}
                className="inline-flex justify-center px-4 py-2 border border-yellow-700 text-sm font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Negotiate
              </button>
              {showNegotiateDropdown && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-100">
                  {canAcceptLower() && (
                    <button
                      onClick={() => handleNegotiate('accept_lower')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Accept at Lower Tier
                    </button>
                  )}
                  {canRequestHigher() && (
                    <button
                      onClick={() => handleNegotiate('request_higher')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Request Higher Tier
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        
        {!isIncoming && (
          <div className="flex items-center justify-end mt-3 pt-3 border-t border-gray-100 md:mt-0 md:pt-0 md:border-t-0 md:ml-4 md:flex-shrink-0">
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              Sent
            </span>
          </div>
        )}
      </div>

      {/* Negotiation Modal */}
      <Modal
        isOpen={isNegotiating}
        onClose={() => setIsNegotiating(false)}
        title={negotiationType === 'accept_lower' ? 'Accept at Lower Tier' : 'Request Higher Tier'}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Tier</label>
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value as FriendshipTier)}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              {getAvailableTiers().map((tier) => (
                <option key={tier} value={tier}>
                  {TIER_LABELS[tier]}
                </option>
              ))}
            </select>
          </div>
          {negotiationType === 'request_higher' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Note (Optional)</label>
              <textarea
                rows={3}
                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Why are you requesting a higher tier?"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={() => setIsNegotiating(false)}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitNegotiation}
              disabled={isSubmitting}
              className="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

